import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { PageHeader, Badge, Select, Money, DateText, Button, Modal, Field, Input, Card, EmptyState, KeyValue } from '../components/ui';
import { Can } from '../components/Can';

type V = Record<string, any>;
const today = () => new Date().toISOString().slice(0, 10);

/**
 * VAT returns.
 *
 * Built from the DOCUMENTS in the period — invoices, bills, credit and debit notes —
 * because that is what an inspector reconciles against. Locking freezes the figures
 * and posts the settlement journal, after which a back-dated document changes the
 * ledger but cannot restate a filed return.
 */
export default function VatReturnsPage() {
  const qc = useQueryClient();
  const [entityId, setEntityId] = useState('');
  const [open, setOpen] = useState<V | null>(null);
  const [filing, setFiling] = useState<V | null>(null);
  const [paying, setPaying] = useState<V | null>(null);
  const [unlocking, setUnlocking] = useState<V | null>(null);
  const [badDebt, setBadDebt] = useState<V | null>(null);
  const [fileForm, setFileForm] = useState({ authorityReference: '', filedOn: today() });
  const [payForm, setPayForm] = useState({ paidOn: today(), bankAccountId: '' });
  const [reason, setReason] = useState('');

  const entities = useQuery({ queryKey: ['entities'], queryFn: async () => (await api.get<V[]>('/entities')).data });
  const target = entityId || entities.data?.[0]?.id || '';
  const banks = useQuery({ queryKey: ['bank-accounts', target], queryFn: async () => (await api.get<V[]>('/bank-accounts', { entityId: target })).data, enabled: !!target });
  const returns = useQuery({ queryKey: ['vat-returns', entityId], queryFn: async () => (await api.get<V[]>('/vat/returns', { entityId: entityId || undefined })).data });
  const detail = useQuery({ queryKey: ['vat-return', open?.id], queryFn: async () => (await api.get<V>(`/vat/returns/${open!.id}`)).data, enabled: !!open });
  const eligible = useQuery({ queryKey: ['bad-debt', badDebt?.entityId], queryFn: async () => (await api.get<V[]>('/vat/bad-debt/eligible', { entityId: badDebt!.entityId })).data, enabled: !!badDebt });

  const refresh = () => { void qc.invalidateQueries({ queryKey: ['vat-returns'] }); if (open) void qc.invalidateQueries({ queryKey: ['vat-return', open.id] }); };

  const generate = useMutation({ mutationFn: async () => api.post('/vat/generate', { entityId: target, year: Number(today().slice(0, 4)) }), onSuccess: refresh });
  const lock = useMutation({ mutationFn: async (id: string) => api.post(`/vat/returns/${id}/lock`, {}), onSuccess: refresh });
  const unlock = useMutation({ mutationFn: async () => api.post(`/vat/returns/${unlocking!.id}/unlock`, { reason }), onSuccess: () => { setUnlocking(null); setReason(''); refresh(); } });
  const file = useMutation({ mutationFn: async () => api.post(`/vat/returns/${filing!.id}/file`, fileForm), onSuccess: () => { setFiling(null); refresh(); } });
  const pay = useMutation({ mutationFn: async () => api.post(`/vat/returns/${paying!.id}/pay`, { paidOn: payForm.paidOn, bankAccountId: payForm.bankAccountId || undefined }), onSuccess: () => { setPaying(null); refresh(); } });
  const claimRelief = useMutation({ mutationFn: async (invoiceId: string) => api.post(`/vat/returns/${badDebt!.id}/bad-debt-relief`, { invoiceId }), onSuccess: () => { void qc.invalidateQueries({ queryKey: ['bad-debt'] }); refresh(); } });

  const d = detail.data;
  const boxes: V[] = d?.computation?.boxes ?? (d?.boxes?.boxes ?? []);
  const warnings: string[] = d?.computation?.warnings ?? d?.boxes?.warnings ?? [];

  return (
    <div>
      <PageHeader
        title="VAT returns"
        subtitle="Prepared from the documents in the period, not from the journals. Draft recalculates live; locking freezes the figures and posts the settlement."
        actions={<Can do="taxReports.create"><div className="flex items-center gap-2">
          <Select value={entityId} onChange={(e) => setEntityId(e.target.value)}><option value="">All companies</option>{entities.data?.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}</Select>
          <Button icon="CalendarPlus" onClick={() => generate.mutate()} disabled={generate.isPending || !target}>Generate this year</Button>
        </div></Can>}
      />

      {generate.isError && <p className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{(generate.error as Error).message}</p>}

      {!returns.data?.length ? (
        <EmptyState icon="Receipt" title="No VAT periods yet" body="Generate the year's periods at the frequency set in Finance settings." />
      ) : (
        <div className="surface overflow-hidden">
          <table className="w-full text-[13px]">
            <thead><tr className="border-b border-ink-100 bg-ink-50/70 text-left text-[10.5px] uppercase tracking-[0.14em] text-ink-400">
              <th className="px-4 py-2.5">Reference</th><th className="px-4 py-2.5">Period</th><th className="px-4 py-2.5">Due</th>
              <th className="px-4 py-2.5">Basis</th><th className="px-4 py-2.5 text-right">Net due</th><th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5" />
            </tr></thead>
            <tbody>
              {returns.data.map((v) => (
                <tr key={v.id} className="cursor-pointer border-b border-ink-50 last:border-0 hover:bg-ink-50/50" onClick={() => setOpen(v)}>
                  <td className="px-4 py-2.5"><span className="font-mono text-[12px] text-ink-600">{v.reference}</span><div className="text-[11px] text-ink-400">{v.entityName}</div></td>
                  <td className="px-4 py-2.5 whitespace-nowrap"><DateText value={v.periodStart} /> — <DateText value={v.periodEnd} /></td>
                  <td className="px-4 py-2.5 whitespace-nowrap"><DateText value={v.dueDate} /></td>
                  <td className="px-4 py-2.5 text-[12px] text-ink-500">{v.basis === 'CASH' ? 'Cash' : 'Accrual'}</td>
                  <td className="px-4 py-2.5 text-right"><Money minor={v.netDueMinor} currency={v.currency} /></td>
                  <td className="px-4 py-2.5"><Badge value={v.status} tone={v.status === 'PAID' ? 'good' : v.status === 'DRAFT' ? 'neutral' : 'warning'} /></td>
                  <td className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
                    <div className="flex justify-end gap-1">
                      {v.status === 'DRAFT' && <Can do="taxReports.lock"><Button size="sm" onClick={() => lock.mutate(v.id)} disabled={lock.isPending}>Lock</Button></Can>}
                      {v.status === 'LOCKED' && <Can do="taxReports.file"><Button size="sm" onClick={() => { setFileForm({ authorityReference: '', filedOn: today() }); setFiling(v); }}>Record filing</Button></Can>}
                      {['LOCKED', 'FILED'].includes(v.status) && <Can do="taxReports.pay"><Button size="sm" variant="secondary" onClick={() => { setPayForm({ paidOn: today(), bankAccountId: '' }); setPaying(v); }}>{v.netDueMinor >= 0 ? 'Pay' : 'Refund'}</Button></Can>}
                      {v.status === 'LOCKED' && <Can do="taxReports.lock"><Button size="sm" variant="ghost" onClick={() => setUnlocking(v)}>Unlock</Button></Can>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {lock.isError && <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{(lock.error as Error).message}</p>}

      {/* Detail */}
      <Modal open={!!open} onClose={() => setOpen(null)} size="lg" title={d?.reference ?? open?.reference ?? 'VAT return'} footer={d && <>
        {d.status === 'DRAFT' && <Can do="taxReports.lock"><Button variant="secondary" onClick={() => setBadDebt(d)}>Bad debt relief</Button></Can>}
        {d.status === 'DRAFT' && <Can do="taxReports.lock"><Button onClick={() => lock.mutate(d.id)} disabled={lock.isPending}>{lock.isPending ? 'Locking…' : 'Lock the period'}</Button></Can>}
      </>}>
        {d && (
          <div className="space-y-4">
            <div className="flex items-center justify-between"><Badge value={d.status} /><span className="text-xs text-ink-400">{d.entityName} · {d.region === 'UK' ? 'HMRC' : 'Federal Tax Authority'} · {d.basis === 'CASH' ? 'cash basis' : 'accrual basis'}</span></div>
            <KeyValue items={[
              { label: 'Period', value: <span><DateText value={d.periodStart} /> — <DateText value={d.periodEnd} /></span> },
              { label: 'Due', value: <DateText value={d.dueDate} /> },
              { label: 'Net due', value: <Money minor={d.netDueMinor ?? d.computation?.netDueMinor} currency={d.currency} /> },
              { label: 'Authority reference', value: d.authorityReference ?? '—' },
            ]} />

            {warnings.map((w, i) => <p key={i} className="rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-[12.5px] leading-relaxed text-amber-900">{w}</p>)}

            <Card title={d.status === 'DRAFT' ? 'Figures — recalculated live' : 'Figures — frozen at lock'}>
              <table className="w-full text-[13px]">
                <thead><tr className="border-b border-ink-100 text-left text-[11px] uppercase text-ink-400"><th className="py-1.5 w-10">Box</th><th className="py-1.5">Description</th><th className="py-1.5 text-right">Net</th><th className="py-1.5 text-right">VAT</th></tr></thead>
                <tbody>
                  {boxes.map((b) => (
                    <tr key={b.key} className="border-b border-ink-50 last:border-0">
                      <td className="py-1.5 font-mono text-[12px] text-ink-500">{b.key}</td>
                      <td className="py-1.5 text-ink-700">{b.label}{b.note && <div className="text-[11px] text-ink-400">{b.note}</div>}</td>
                      <td className="py-1.5 text-right">{b.netMinor ? <Money minor={b.netMinor} currency={d.currency} /> : <span className="text-ink-300">—</span>}</td>
                      <td className="py-1.5 text-right font-medium">{b.taxMinor ? <Money minor={b.taxMinor} currency={d.currency} /> : <span className="text-ink-300">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>

            {d.computation?.sources && (
              <p className="text-[12px] text-ink-500">
                From {d.computation.sources.salesInvoices} sales invoice(s), {d.computation.sources.salesNotes} sales note(s),
                {' '}{d.computation.sources.purchaseBills} bill(s), {d.computation.sources.purchaseNotes} purchase note(s)
                {d.computation.sources.adjustments ? ` and ${d.computation.sources.adjustments} adjustment(s)` : ''}.
              </p>
            )}

            {!!d.adjustments?.length && (
              <Card title="Adjustments">
                <ul className="space-y-1.5 text-[12.5px]">
                  {d.adjustments.map((a: V) => (
                    <li key={a.id} className="flex items-center justify-between"><span className="text-ink-700">Box {a.box} · {String(a.kind).replace(/_/g, ' ').toLowerCase()}{a.note ? ` — ${a.note}` : ''}</span><Money minor={a.taxMinor} currency={d.currency} /></li>
                  ))}
                </ul>
              </Card>
            )}

            <p className="rounded-xl border border-ink-200 bg-ink-50/60 px-3.5 py-2.5 text-[12px] leading-relaxed text-ink-600">
              <strong className="font-medium text-ink-800">Not implemented — transmission.</strong> Nothing here files to HMRC Making Tax Digital or the FTA portal; both need a recognised submission channel and credentials. The return is prepared, frozen and exportable, and the reference the authority gives back is recorded here.
            </p>
          </div>
        )}
      </Modal>

      {/* File */}
      <Modal open={!!filing} onClose={() => setFiling(null)} title="Record the filing" footer={<>
        <Button variant="secondary" onClick={() => setFiling(null)}>Cancel</Button>
        <Button onClick={() => file.mutate()} disabled={file.isPending}>Record as filed</Button>
      </>}>
        <p className="mb-3 text-[13px] text-ink-600">Enter the receipt number the authority returned after you submitted it.</p>
        <div className="space-y-3">
          <Field label="Authority reference"><Input value={fileForm.authorityReference} onChange={(e) => setFileForm({ ...fileForm, authorityReference: e.target.value })} placeholder="HMRC-123456789" className="w-full" /></Field>
          <Field label="Filed on"><Input type="date" value={fileForm.filedOn} onChange={(e) => setFileForm({ ...fileForm, filedOn: e.target.value })} className="w-full" /></Field>
        </div>
        {file.isError && <p className="mt-2 text-xs text-red-600">{(file.error as Error).message}</p>}
      </Modal>

      {/* Pay */}
      <Modal open={!!paying} onClose={() => setPaying(null)} title={(paying?.netDueMinor ?? 0) >= 0 ? 'Pay the VAT' : 'Record the refund'} footer={<>
        <Button variant="secondary" onClick={() => setPaying(null)}>Cancel</Button>
        <Button onClick={() => pay.mutate()} disabled={pay.isPending}>Post</Button>
      </>}>
        {paying && (
          <div className="space-y-3">
            <p className="text-[13px] text-ink-600">Posts <strong>{paying.netDueMinor >= 0 ? 'Dr VAT control / Cr Bank' : 'Dr Bank / Cr VAT control'}</strong> for <Money minor={Math.abs(paying.netDueMinor)} currency={paying.currency} />.</p>
            <Field label="Date"><Input type="date" value={payForm.paidOn} onChange={(e) => setPayForm({ ...payForm, paidOn: e.target.value })} className="w-full" /></Field>
            <Field label="Bank account"><Select value={payForm.bankAccountId} onChange={(e) => setPayForm({ ...payForm, bankAccountId: e.target.value })} className="w-full"><option value="">Default bank</option>{banks.data?.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}</Select></Field>
            {pay.isError && <p className="text-xs text-red-600">{(pay.error as Error).message}</p>}
          </div>
        )}
      </Modal>

      {/* Unlock */}
      <Modal open={!!unlocking} onClose={() => { setUnlocking(null); setReason(''); }} title="Unlock the period" footer={<>
        <Button variant="secondary" onClick={() => { setUnlocking(null); setReason(''); }}>Cancel</Button>
        <Button variant="danger" onClick={() => unlock.mutate()} disabled={!reason || unlock.isPending}>Unlock</Button>
      </>}>
        <p className="mb-3 text-[13px] text-ink-600">Reverses the settlement journal and returns the return to draft. Only possible before filing — a filed return is corrected in the next period, never unlocked.</p>
        <Field label="Reason"><Input value={reason} onChange={(e) => setReason(e.target.value)} className="w-full" /></Field>
        {unlock.isError && <p className="mt-2 text-xs text-red-600">{(unlock.error as Error).message}</p>}
      </Modal>

      {/* Bad debt relief */}
      <Modal open={!!badDebt} onClose={() => setBadDebt(null)} size="lg" title="VAT bad debt relief" footer={<Button variant="secondary" onClick={() => setBadDebt(null)}>Close</Button>}>
        <p className="mb-3 text-[13px] leading-relaxed text-ink-600">
          Output tax already accounted for on an invoice that was never paid can be reclaimed once the debt is more than six months past due. Claiming here reclaims the VAT on the UNPAID portion and records the adjustment against this return. The debt itself is written off separately, by credit note.
        </p>
        {!eligible.data?.length ? (
          <p className="rounded-xl bg-ink-50 px-3.5 py-3 text-[13px] text-ink-600">Nothing is eligible — no unpaid VAT-bearing invoice is old enough, or relief has already been claimed on the ones that are.</p>
        ) : (
          <table className="w-full text-[13px]">
            <thead><tr className="border-b border-ink-100 text-left text-[11px] uppercase text-ink-400"><th className="py-1.5">Invoice</th><th className="py-1.5">Customer</th><th className="py-1.5">Due</th><th className="py-1.5 text-right">Outstanding</th><th className="py-1.5 text-right">Relief</th><th /></tr></thead>
            <tbody>
              {eligible.data.map((e) => (
                <tr key={e.invoiceId} className="border-b border-ink-50">
                  <td className="py-1.5 font-mono text-[12px] text-ink-600">{e.number}</td>
                  <td className="py-1.5 text-ink-700">{e.customerName ?? '—'}</td>
                  <td className="py-1.5 text-ink-500"><DateText value={e.dueDate} /><span className="ml-1 text-[11px] text-ink-400">{e.daysOverdue}d</span></td>
                  <td className="py-1.5 text-right"><Money minor={e.outstandingMinor} currency={e.currency} /></td>
                  <td className="py-1.5 text-right font-medium"><Money minor={e.reliefTaxMinor} currency={e.currency} /></td>
                  <td className="py-1.5 pl-3 text-right"><Button size="sm" onClick={() => claimRelief.mutate(e.invoiceId)} disabled={claimRelief.isPending}>Claim</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {claimRelief.isError && <p className="mt-2 text-xs text-red-600">{(claimRelief.error as Error).message}</p>}
      </Modal>
    </div>
  );
}
